import { useCallback, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router';

import { checkCode } from '@psy/validations';

import { useLanguage } from '../../contexts/language';
import { useSession } from '../../contexts/session';
import { useField, validateAll } from '../../hooks/field';
import { useRequest } from '../../hooks/request';

import { Button } from '../button';
import { Centered } from '../centered';
import { PageTitle } from '../page-title';
import { ResendCode } from '../resend-code';
import { TextField } from '../text-field';

import styles from './style.module.css';

// Keeps only digits, at most six, so a code pasted as "123 456" or "123-456"
// lands as 123456. Declared outside the component so the field receives the
// same function on every render.
function digits(value) {
  return value.replace(/\D/g, '').slice(0, 6);
}

// The second half of signing in: the 6-digit code from the email. A correct
// code sets the session cookie; refreshing the session is then all it takes,
// because the router moves a signed-in person on to onboarding or Today.
//
// One field rather than six boxes: it accepts a pasted code and the phone's
// one-time-code autofill, which six separate inputs fight.
export function CheckCode() {
  const { text, translate } = useLanguage();
  const { refresh } = useSession();
  const request = useRequest();
  const location = useLocation();
  const code = useField({ initial: '', check: checkCode, clean: digits });
  const [ checking, setChecking ] = useState(false);

  // A missing state and a state without an address are the same absence here:
  // nobody came from the sign-in screen, so there is no address to check a
  // code against.
  const { email } = location.state || {};

  const onSubmit = useCallback(function(event) {
    event.preventDefault();

    if (validateAll([ code ]) === false) {
      return null;
    }

    setChecking(true);

    // A wrong, expired or used-up code is only known to the service, so its
    // refusal goes under the field the same way a local one does.
    return request({ path: '/auth/session', method: 'POST', body: { email, code: code.value } }).then(function() {
      return refresh();
    }).catch(function(failed) {
      setChecking(false);
      code.refuse(failed.message);
    });
  }, [ email, code, request, refresh ]);

  if (typeof email !== 'string') {
    return <Navigate to='/signin' replace/>;
  }

  return (
    <Centered>
      <PageTitle title={ text.codeTitle } intro={ `${ text.codeIntro } ${ email }.` }/>

      <form className={ styles.form } onSubmit={ onSubmit } noValidate>
        <TextField
          ref={ code.ref }
          id='check-code'
          label={ text.code }
          value={ code.value }
          onChange={ code.onChange }
          onBlur={ code.onBlur }
          error={ translate(code.error) }
          autoComplete='one-time-code'
          inputMode='numeric'
        />

        <Button type='submit' disabled={ checking }>{ text.continue }</Button>
      </form>

      <div className={ styles.links }>
        <ResendCode email={ email }/>

        <Link className={ styles.link } to='/signin'>{ text.differentEmail }</Link>
      </div>
    </Centered>
  );
}
